<template>
  <div
    ref="galleryContainer"
    class="Gallery"
    :class="{ '-long': tooManyAttachments && hidingLong && !overflowGrid }"
  >
    <div class="gallery-rows">
      <button
        v-if="nsfwHidden && size !== 'hide'"
        class="button-unstyled gallery-nsfw-overlay"
        :title="$t('status.sensitive_content_show')"
        @click.prevent.stop="onOverlayClick"
      >
        <span class="gallery-nsfw-pill">
          <FAIcon icon="eye" />
          <template v-if="size !== 'small'">
            <span>{{ $t('status.sensitive_content') }}</span>
            <span class="gallery-nsfw-action">{{ $t('status.sensitive_content_show') }}</span>
          </template>
        </span>
      </button>
      <div
        v-for="(row, rowIndex) in rows"
        :key="rowIndex"
        class="gallery-row"
        :style="rowStyle(row)"
        :inert="nsfwHidden && size !== 'hide'"
        :class="{ '-audio': row.audio, '-player': row.audio && useAudioPlayer, '-minimal': row.minimal, '-grid': row.grid }"
      >
        <div
          class="gallery-row-inner"
          :class="{ '-grid': row.grid, '-overflow': row.overflow }"
        >
          <AudioPlayer
            v-if="row.audio && useAudioPlayer"
            class="gallery-item"
            :tracks="row.items"
            @play="(id) => onMediaStateChange(true, id)"
            @pause="(id) => onMediaStateChange(false, id)"
          />
          <Attachment
            v-for="(attachment, attachmentIndex) in (row.audio && useAudioPlayer ? [] : row.items)"
            :key="attachment.id"
            class="gallery-item"
            :compact="compact"
            :nsfw="nsfw"
            :revealed="nsfwRevealed"
            :attachment="attachment"
            :size="row.overflow ? 'small' : size"
            :editable="editable"
            :remove="removeAttachment"
            :shift-up="!(attachmentIndex === 0 && rowIndex === 0) && shiftUpAttachment"
            :shift-dn="!(attachmentIndex === row.items.length - 1 && rowIndex === rows.length - 1) && shiftDnAttachment"
            :edit="editAttachment"
            :description="descriptions && descriptions[attachment.id]"
            :hide-description="size === 'small' || tooManyAttachments && hidingLong"
            :style="itemStyle(attachment.id, row.items)"
            @set-media="onMedia"
            @update:revealed="setNsfwRevealed"
            @natural-size-load="onNaturalSizeLoad"
            @play="() => onMediaStateChange(true, attachment.id)"
            @pause="() => onMediaStateChange(false, attachment.id)"
          />
          <button
            v-if="row.overflow"
            class="button-unstyled gallery-overflow-tile"
            :title="$t('status.open_gallery')"
            :aria-label="$t('status.more_attachments', { number: row.overflow }, row.overflow)"
            @click.prevent="openOverflow"
          >
            +{{ row.overflow }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="tooManyAttachments"
      class="many-attachments"
    >
      <div
        v-if="!(overflowGrid && hidingLong)"
        class="many-attachments-text"
      >
        {{ $t("status.many_attachments", { number: attachments.length }) }}
      </div>
      <div class="many-attachments-buttons">
        <span
          v-if="!hidingLong"
          class="many-attachments-button"
        >
          <button
            class="button-unstyled -link"
            @click="toggleHidingLong(true)"
          >
            {{ $t("status.collapse_attachments") }}
          </button>
        </span>
        <span
          v-if="hidingLong"
          class="many-attachments-button"
        >
          <button
            class="button-unstyled -link"
            @click="toggleHidingLong(false)"
          >
            {{ $t("status.show_all_attachments") }}
          </button>
        </span>
        <span
          v-if="hidingLong && !overflowGrid"
          class="many-attachments-button"
        >
          <button
            class="button-unstyled -link"
            @click="openGallery"
          >
            {{ $t("status.open_gallery") }}
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script src='./gallery.js'></script>

<style lang="scss">
.Gallery {
  .gallery-rows {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .gallery-nsfw-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: none;

    &:focus-visible .gallery-nsfw-pill {
      outline: 2px solid var(--link);
      outline-offset: 2px;
    }
  }

  .gallery-nsfw-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.5em 1em;
    border-radius: var(--r-pill);
    background: var(--scrim);
    color: rgb(255 255 255 / 92%);
    font-weight: 600;
    line-height: 1.4;
    max-width: calc(100% - 2em);
  }

  .gallery-nsfw-action {
    font-weight: 400;
    opacity: 0.8;
  }

  .gallery-row {
    position: relative;
    height: 0;
    width: 100%;
    flex-grow: 1;

    .gallery-row-inner {
      position: absolute;
      inset: 0;
      display: flex;
      flex-flow: row wrap;
      align-content: stretch;

      .gallery-item {
        margin: 0 0.5em 0 0;
        flex-grow: 1;
        height: 100%;
        box-sizing: border-box;
        // to make failed images a bit more noticeable on chromium
        min-width: 2em;

        &:last-child {
          margin: 0;
        }
      }

      &.-grid {
        width: 100%;
        height: auto;
        position: relative;
        display: grid;
        gap: 0.5em;
        grid-template-columns: repeat(auto-fill, minmax(15em, 1fr));

        .gallery-item {
          margin: 0;
          height: 20em;
        }

        // 2 rows x 4 columns, see OVERFLOW_GRID_CELLS in gallery.js
        &.-overflow {
          grid-template-columns: repeat(4, 1fr);

          .gallery-item {
            height: auto;
            aspect-ratio: 1;

            img,
            canvas,
            video {
              object-fit: cover;
            }
          }

          // the "+N" tile sits on top of the last thumbnail
          .gallery-item:nth-child(8),
          .gallery-overflow-tile {
            grid-area: 2 / 4;
          }

          .gallery-overflow-tile {
            position: relative;
            z-index: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            color: rgb(255 255 255 / 90%);
            text-shadow: 0 0 0.25em var(--scrim-strong);
            background: var(--scrim);
            border-radius: var(--roundness);
            box-shadow: none;
            font-size: 2em;
            font-weight: bold;
            cursor: pointer;

            &:hover,
            &:focus-visible {
              background: var(--scrim-soft);
            }
          }
        }
      }
    }

    &.-grid,
    &.-minimal,
    &.-player {
      height: auto;

      .gallery-row-inner {
        position: relative;
      }
    }

    &:not(:first-child) {
      margin-top: 0.5em;
    }
  }

  &.-long {
    .gallery-rows {
      max-height: 25em;
      overflow: hidden;
      mask:
        linear-gradient(to top, white, transparent) bottom/100% 70px no-repeat,
        linear-gradient(to top, white, white);

      /* Autoprefixed seem to ignore this one, and also syntax is different */
      /* stylelint-disable mask-composite */
      /* stylelint-disable declaration-property-value-no-unknown */
      /* stylelint-disable scss/declaration-property-value-no-unknown */

      /* TODO check if this is still needed */
      mask-composite: xor;
      /* stylelint-enable scss/declaration-property-value-no-unknown */
      /* stylelint-enable declaration-property-value-no-unknown */
      /* stylelint-enable mask-composite */
      mask-composite: exclude;
    }
  }

  .many-attachments-text {
    text-align: center;
    line-height: 2;
  }

  .many-attachments-buttons {
    display: flex;
  }

  .many-attachments-button {
    display: flex;
    flex: 1;
    justify-content: center;
    line-height: 2;

    button {
      padding: 0 2em;
    }
  }
}
</style>
